import React from 'react';
import { CmhcIcon, CmhcIconSize } from '@cmhc-schl/hearth-ui';
import { Tooltip } from '@mui/material';
import { TFunction } from 'i18next';
import { MRT_ColumnDef } from 'material-react-table';
import styles from '../styles.module.scss';
import { LoanInventoryTableFields } from '../types';
import {
  renderDefaultCell,
  renderLoanStatusCell,
  createInsuredLenderNameRenderer,
  renderCmhcAccountNumberCell,
  renderRemark
} from './loansInventoryTableCellRenderers';
import { renderDataTestId } from 'Feature/V2/HbtPortfolioManagement/components/HbtPortfolioManagementTable/vriTableColumns';

export const createLoansInventoryColumns = (
  isCancellation: boolean,
  t: TFunction,
  language: string
): MRT_ColumnDef<LoanInventoryTableFields>[] => {
  const insuredLenderNameRenderer = createInsuredLenderNameRenderer(language);
  const cmhcAccountNumberCellRenderer = renderCmhcAccountNumberCell(t);
  const LoanStatusCellRenderer = renderLoanStatusCell(t);

  return [
    ...(!isCancellation
      ? []
      : [
          {
            accessorKey: 'cmhcAccountNumber' as const,
            header: t('CMHC-Account-Number'),
            Cell: cmhcAccountNumberCellRenderer,
            enableSorting: true,
            size: language === 'en' ? 260 : 275,
            muiTableHeadCellProps: renderDataTestId('cmhcAccountNumber')
          }
        ]),
    {
      accessorKey: 'lenderReferenceNumber' as const,
      header: t('Lender-Reference-Number'),
      Cell: renderDefaultCell,
      enableSorting: true,
      size: language === 'en' ? 275 : 315,
      muiTableHeadCellProps: renderDataTestId('lenderReferenceNumber')
    },
    {
      accessorKey: 'loanStatus' as const,
      header: t('Loan-Status'),
      Cell: LoanStatusCellRenderer,
      enableSorting: true,
      size: language === 'en' ? 204 : 204,
      muiTableHeadCellProps: renderDataTestId('loanStatus')
    },
    ...(!isCancellation
      ? [
          {
            accessorKey: 'cmhcAccountNumber' as const,
            header: t('CMHC-Low-Ratio-Account-Number'),
            muiTableBodyCellProps(props: { cell: { getValue: () => any } }) {
              return {
                'aria-label':
                  language === 'en'
                    ? `CMHC Low Ratio Account Number ${props.cell.getValue()}`
                    : `Caractéristiques du ratio faible CMHC ${props.cell.getValue()}`
              };
            },
            Cell: cmhcAccountNumberCellRenderer,
            enableSorting: true,
            size: language === 'en' ? 335 : 335,
            muiTableHeadCellProps: renderDataTestId('cmhcLowRatioAccountNumber')
          }
        ]
      : []),
    ...(!isCancellation
      ? [
          {
            accessorKey: 'propertyAddress' as const,
            header: t('Property-Address'),
            Header: ({ column }: any) => {
              return (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <div>{column.columnDef?.header}</div>
                  <Tooltip
                    title={t('Property-Address-Tool-Tip-Text')}
                    placement="top-start"
                    arrow
                    slotProps={{
                      tooltip: {
                        className: styles.loansInventoryToolTip
                      },
                      arrow: {
                        className: styles.tooltipArrow
                      }
                    }}
                  >
                    <div
                      style={{
                        display: 'inline-block',
                        marginLeft: '8px'
                      }}
                    >
                      <CmhcIcon size={CmhcIconSize.SMALL} type="icon_info_outlined" />
                    </div>
                  </Tooltip>
                </div>
              );
            },
            Cell: renderDefaultCell,
            enableSorting: false,
            size: language === 'en' ? 235 : 275,
            muiTableHeadCellProps: renderDataTestId('propertyAddress')
          }
        ]
      : []),
    {
      accessorKey: 'portfolioId' as const,
      header: t('Portfolio-ID'),
      Cell: renderDefaultCell,
      enableSorting: true,
      size: language === 'en' ? 170 : 210,
      muiTableHeadCellProps: renderDataTestId('portfolioId')
    },
    ...(!isCancellation
      ? [
          {
            accessorKey: 'underwritingApprovedLenderCode' as const,
            header: t('Under-Writing-Approved-Lender-Code'),
            Cell: renderDefaultCell,
            enableSorting: true,
            size: language === 'en' ? 345 : 420,
            muiTableHeadCellProps: renderDataTestId('underwritingApprovedLenderCode')
          }
        ]
      : [
          {
            accessorKey: 'insuredApprovedLenderCode' as const,
            header: t('Insured-Approved-Lender-Code'),
            Cell: renderDefaultCell,
            enableSorting: true,
            size: 305,
            muiTableHeadCellProps: renderDataTestId('insuredApprovedLenderCode')
          }
        ]),
    ...(!isCancellation
      ? [
          {
            accessorKey: 'approvedLender' as const,
            header: t('Underwriting-Approved-Lender-Name'),
            Cell: insuredLenderNameRenderer,
            enableSorting: true,
            size: language === 'en' ? 355 : 420,
            muiTableHeadCellProps: renderDataTestId('approvedLender')
          }
        ]
      : [
          {
            accessorKey: 'approvedLender' as const,
            header: t('Insured-Approved-Lender-Name'),
            Cell: insuredLenderNameRenderer,
            enableSorting: true,
            size: language === 'en' ? 325 : 305,
            muiTableHeadCellProps: renderDataTestId('approvedLender')
          }
        ]),
    {
      accessorKey: 'latestRemark' as const,
      header: t('Remarks'),
      muiTableBodyCellProps(props) {
        return {
          'aria-label': language === 'en' ? `remarks ${props.row.id}` : `remarques ${props.row.id}`
        };
      },
      Cell: renderRemark,
      enableSorting: true,
      size: language === 'en' ? 150 : 170,
      muiTableHeadCellProps: renderDataTestId('latestRemark')
    }
  ];
};
